/**
 * Executive Assistant — schedule send-at: wall clock in IANA zone ↔ UTC instant.
 * Used by Schedule email + Edit scheduled email; keep in sync with API `sendAt` (ISO UTC).
 */

export function getDeviceIanaTimeZoneId(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function isValidIanaTimeZone(timeZone: string): boolean {
  const tz = String(timeZone || '').trim();
  if (!tz) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz }).format(0);
    return true;
  } catch {
    return false;
  }
}

/** Calendar date `YYYY-MM-DD` for `at` interpreted in IANA `timeZone` (e.g. “today” in that zone). */
export function calendarDateTodayInTimeZone(timeZone: string, at: Date = new Date()): string | null {
  if (!isValidIanaTimeZone(timeZone)) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(at);
    const y = parts.find((p) => p.type === 'year')?.value;
    const m = parts.find((p) => p.type === 'month')?.value;
    const d = parts.find((p) => p.type === 'day')?.value;
    if (y && m && d) return `${y}-${m}-${d}`;
  } catch {
    return null;
  }
  return null;
}

/**
 * UTC instant when the wall clock in IANA `timeZone` reads y-mo-d h:mi.
 * Returns null if that local time does not exist (spring-forward gap) or on invalid zone.
 */
export function zonedWallTimeToUtc(
  y: number,
  mo: number,
  d: number,
  h: number,
  mi: number,
  timeZone: string
): Date | null {
  if (!isValidIanaTimeZone(timeZone)) return null;
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });

  const read = (instantMs: number) => {
    const parts = formatter.formatToParts(new Date(instantMs));
    const get = (type: string) => parseInt(parts.find((p) => p.type === type)?.value || 'NaN', 10);
    return {
      y: get('year'),
      mo: get('month'),
      d: get('day'),
      h: get('hour'),
      mi: get('minute'),
    };
  };

  const base = Date.UTC(y, mo - 1, d, 12, 0, 0);
  for (let offsetMs = -86400000; offsetMs <= 86400000; offsetMs += 60000) {
    const t = base + offsetMs;
    const g = read(t);
    if (g.y === y && g.mo === mo && g.d === d && g.h === h && g.mi === mi) {
      return new Date(t);
    }
  }
  return null;
}

export function parseScheduleSendAt(sendDate: string, sendTime: string, timeZone: string): Date | null {
  const dStr = String(sendDate || '').trim();
  const tStr = String(sendTime || '').trim();
  const tz = String(timeZone || '').trim();
  if (!dStr || !tStr || !tz) return null;
  const [ys, ms, ds] = dStr.split('-').map((x) => parseInt(x, 10));
  const timeParts = tStr.split(':');
  const hs = parseInt(timeParts[0] || '0', 10);
  const mis = parseInt(timeParts[1] || '0', 10);
  if (!Number.isFinite(ys) || !Number.isFinite(ms) || !Number.isFinite(ds) || !Number.isFinite(hs) || !Number.isFinite(mis)) {
    return null;
  }
  return zonedWallTimeToUtc(ys, ms, ds, hs, mis, tz);
}

/** Calendar date `YYYY-MM-DD` and clock `HH:mm` for `iso` (UTC instant) in IANA `timeZone`. */
export function utcInstantToZonedWallStrings(iso: string, timeZone: string): { dateStr: string; timeStr: string } | null {
  if (!isValidIanaTimeZone(timeZone)) return null;
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return null;
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const parts = formatter.formatToParts(instant);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  const y = get('year');
  const mo = get('month');
  const d = get('day');
  const h = get('hour');
  const mi = get('minute');
  if (!y || !mo || !d) return null;
  return {
    dateStr: `${y}-${mo}-${d}`,
    timeStr: `${h.padStart(2, '0')}:${mi.padStart(2, '0')}`,
  };
}
